
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { validateVehicleForm, createVehiclePayload, VehicleFormData } from "../utils/formValidation";
import { registerVehicle } from "../api/vehicleAPI";

export const useVehicleForm = () => {
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleYear, setVehicleYear] = useState("");
  const [vehicleColor, setVehicleColor] = useState("");
  const [vehicleCapacity, setVehicleCapacity] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [vehicleMake, setVehicleMake] = useState("");
  const [licensePlateRegion, setLicensePlateRegion] = useState("");
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [attestation, setAttestation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [continueButtonPath, setContinueButtonPath] = useState("");
  const [continueButtonLoaded, setContinueButtonLoaded] = useState(false);
  
  const { toast } = useToast();
  const navigate = useNavigate();
  
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      console.warn("No authentication token found in localStorage");
      toast({
        title: "Authentication required",
        description: "Please log in to register your vehicle",
        variant: "destructive",
      });
      navigate("/login");
    } else {
      setToken(authToken);
    }
    
    // Direct path to continue_cta.png
    const buttonImagePath = "/continue_cta.png";
    setContinueButtonPath(buttonImagePath);
    
    // Test if the image loads correctly
    const testImage = new Image();
    testImage.onload = () => {
      console.log("✅ Continue button image loaded successfully:", buttonImagePath);
    };
    testImage.onerror = () => {
      console.error("❌ Failed to load continue button image:", buttonImagePath);
      // Fallback to alternative paths if needed
      const fallbackPaths = [
        "/assets/continue_cta.png", 
        "continue_cta.png"
      ];
      
      // Try each fallback path
      const tryNextPath = (index: number) => {
        if (index >= fallbackPaths.length) {
          console.error("All continue button image paths failed to load");
          return;
        }
        
        const path = fallbackPaths[index];
        const fallbackImage = new Image();
        fallbackImage.onload = () => {
          console.log("✅ Fallback continue button image loaded:", path);
          setContinueButtonPath(path);
        };
        fallbackImage.onerror = () => {
          console.error("❌ Fallback path failed:", path);
          tryNextPath(index + 1);
        };
        fallbackImage.src = path;
      };
      
      tryNextPath(0);
    };
    testImage.src = buttonImagePath;
  }, [navigate, toast]);
  
  const toggleAmenity = (amenityId: string) => {
    setSelectedAmenities(prev => 
      prev.includes(amenityId)
        ? prev.filter(id => id !== amenityId)
        : [...prev, amenityId]
    );
  };
  
  const getFormData = (): VehicleFormData => ({
    vehicleType,
    vehicleYear,
    vehicleColor,
    vehicleCapacity,
    licensePlate,
    vehicleMake,
    licensePlateRegion,
    selectedAmenities,
    attestation
  });
  
  const resetForm = () => {
    setVehicleType("");
    setVehicleYear("");
    setVehicleColor("");
    setVehicleCapacity("");
    setLicensePlate("");
    setVehicleMake("");
    setLicensePlateRegion("");
    setSelectedAmenities([]);
    setAttestation(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      toast({
        title: "Authentication required",
        description: "Please log in to register your vehicle",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }
    
    const formData = getFormData();
    if (!validateVehicleForm(formData, toast)) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const vehicleData = createVehiclePayload(formData);
      console.log("Submitting vehicle data:", vehicleData);
      
      await registerVehicle(vehicleData, token);
      
      toast({
        title: "Vehicle registered",
        description: "Your vehicle has been successfully registered",
      });
      
      // Navigate to dashboard after successful registration
      navigate("/dashboard");
    } catch (error) {
      toast({
        title: "Registration failed",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleAddAnotherVehicle = async () => {
    if (!token) {
      toast({
        title: "Authentication required",
        description: "Please log in to register your vehicle",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }
    
    const formData = getFormData();
    if (!validateVehicleForm(formData, toast)) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const vehicleData = createVehiclePayload(formData);
      console.log("Adding another vehicle with data:", vehicleData);
      
      await registerVehicle(vehicleData, token);
      
      toast({
        title: "Vehicle registered",
        description: "Your vehicle has been successfully registered",
      });
      
      resetForm();
    } catch (error) {
      toast({
        title: "Registration failed",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formProps: {
      vehicleMake,
      setVehicleMake,
      vehicleType,
      setVehicleType,
      vehicleColor,
      setVehicleColor,
      vehicleYear,
      setVehicleYear,
      vehicleCapacity,
      setVehicleCapacity,
      licensePlate,
      setLicensePlate,
      licensePlateRegion,
      setLicensePlateRegion,
      selectedAmenities,
      attestation,
      setAttestation
    },
    isSubmitting,
    toggleAmenity,
    handleSubmit,
    handleAddAnotherVehicle,
    navigate,
    continueButtonPath,
    continueButtonLoaded,
    setContinueButtonLoaded
  };
};
